import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { useSubmitField } from '../../hooks/useSubmitField';
import { SubmitFormContext } from '../../context';

import s from './AddWatermarkField.scss';

export const MIN_SIZE_FOR_WATERMARK = 400;

const AddWatermarkField: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { initResponse } = useContext(SubmitFormContext)!;
  const { displayOptions } = initResponse!;

  // First display option is the image original size
  // See DisplayOptionsField for more information
  let disabled = false;
  if (displayOptions && displayOptions.length > 0) {
    const { width, height } = displayOptions[0];
    disabled =
      parseInt(`${width}`) < MIN_SIZE_FOR_WATERMARK ||
      parseInt(`${height}`) < MIN_SIZE_FOR_WATERMARK;
  }

  const { setValue, watch } = useFormContext();
  const displayResolution = watch('displayResolution');
  const {
    id,
    field: { onChange, value },
  } = useSubmitField('addWatermark', {
    initAdapter: ({ deviation }) => deviation?.extended?.hasWatermark ?? false,
    submitAdapter: ({ addWatermark }) => ({ add_watermark: addWatermark }),
  });

  const handleChange = useCallback(newValue => {
    if (newValue) {
      // Disable free download if watermark is turned on
      setValue('allowFreeDownload', false, { shouldDirty: true });

      // If display resolution was Original and watermark is turned on
      // set resolution to highest available <= 1024 width
      if (displayOptions && displayResolution === '0') {
        let highestOption;
        for (let i = 1; i < displayOptions.length; i++) {
          if (displayOptions[i].width > 1024) {
            break;
          }
          highestOption = i;
        }
        setValue('displayResolution', highestOption, {
          shouldDirty: true,
        });
      }
    }
    onChange(newValue);
  }, [setValue, onChange, displayOptions, displayResolution]);

  return (
    <div className={s['root']}>
      <BooleanInput
        id={id}
        label={t('deviationSubmit.form.addWatermark.label')}
        onChange={handleChange}
        value={value}
        size={isMobile ? 'large' : 'small'}
        noBottomSpacing
        className={className}
        disabled={disabled}
      />
      {disabled && (
        <SimpleTooltip
          usePortal
          placement="top"
          text={t('deviationSubmit.form.addWatermark.disabled.tooltip')}
        >
          <InfoIcon size={IconSize.SMALLER} disableDefaultColors />
        </SimpleTooltip>
      )}
    </div>
  );
};
AddWatermarkField.displayName = 'AddWatermarkField';

export default AddWatermarkField;
